import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { ProductContextDirective } from 'ish-core/directives/product-context.directive';
import { LineItemView } from 'ish-core/models/line-item/line-item.model';
import { OrderLineItem } from 'ish-core/models/order/order.model';
import { Price } from 'ish-core/models/price/price.model';
import { PricePipe } from 'ish-core/models/price/price.pipe';
import { PagingComponent } from 'ish-shared/components/common/paging/paging.component';
import { LineItemListElementComponent } from 'ish-shared/components/line-item/line-item-list-element/line-item-list-element.component';

/**
 * The Line Item List Component displays line items of orders and baskets.
 * It provides optional delete and edit functionality
 * It provides optional lineItemView (string 'simple')
 * It provides optional total cost output
 *
 * @example
 * <ish-line-item-list
 *   [lineItems]="lineItems"
 *   [editable]="editable"
 *   [total]="total"
 *   lineItemViewType="simple"  // simple = no edit-button, inventory, shipment
 * ></ish-line-item-list>
 */
@Component({
  selector: 'ish-line-item-list',
  templateUrl: './line-item-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    LineItemListElementComponent,
    ProductContextDirective,
    PagingComponent,
    PricePipe,
    TranslateModule,
  ],
})
export class LineItemListComponent implements OnChanges {
  readonly lineItems = input.required<Partial<LineItemView & OrderLineItem>[]>();
  readonly editable = input(true);
  readonly total = input<Price>(undefined);
  readonly lineItemViewType = input<'simple' | 'availability'>(undefined);
  /**
   * If pageSize > 0 only <pageSize> items are shown at once and a paging bar is shown below the line item list.
   */
  readonly pageSize = input(25);

  currentPage = 1;
  lastPage: number;
  displayItems: Partial<LineItemView & OrderLineItem>[] = [];

  ngOnChanges(c: SimpleChanges) {
    if (c.lineItems) {
      this.lastPage = Math.ceil(this.lineItems()?.length / this.pageSize());
      this.goToPage(this.currentPage);
    }
  }

  get showPagingBar() {
    return this.pageSize() && this.lineItems()?.length > this.pageSize();
  }

  trackByFn(_: number, item: Partial<LineItemView & OrderLineItem>) {
    return item.id;
  }

  /**
   * Refresh items to display after changing the current page
   *
   * @param page current page
   */
  goToPage(page: number) {
    this.currentPage = page;

    this.displayItems = this.pageSize
      ? this.lineItems().slice((page - 1) * this.pageSize(), page * this.pageSize())
      : this.lineItems();
  }
}
